import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowLeft, Check, Pause, Play, Trash2, CalendarClock, Sparkles,
    Edit3, X as XIcon,
} from "lucide-react";
import {
    getGoal, updateGoal, deleteGoal, checkinGoal, goalEncouragement,
} from "@/lib/api";
import { ALL_SLOTS } from "./constants";
import {
    fmtTime, daysUntil, formatHorizon, formatLongDate,
    legacyRhythmConfig, scheduledSlotsForToday,
} from "./helpers";
import CheckinRow from "./CheckinRow";
import QuantityTracker from "./QuantityTracker";
import WeeklyReflectCard from "./WeeklyReflectCard";

export default function GoalView({ id }) {
    const nav = useNavigate();
    const [goal, setGoal] = useState(null);
    const [checkins, setCheckins] = useState([]);
    const [encouragement, setEncouragement] = useState(null);
    const [note, setNote] = useState("");
    const [showBackdate, setShowBackdate] = useState(false);
    const [backdateAt, setBackdateAt] = useState("");
    const [busy, setBusy] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [crossroadsDismissed, setCrossroadsDismissed] = useState(false);
    const [pickingNewDate, setPickingNewDate] = useState(false);
    const [newHorizon, setNewHorizon] = useState("");

    const load = async () => {
        const d = await getGoal(id);
        setGoal(d.goal);
        setCheckins(d.checkins || []);
        if (d.goal) {
            const e = await goalEncouragement(d.goal.category, d.goal.theme, d.goal.passage_cursor || 0);
            setEncouragement(e);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    // Legacy times-of-day: which slots have been checked in today?
    const todaySlotState = useMemo(() => {
        if (!goal || goal.rhythm !== "times_of_day" || goal.rhythm_config) return {};
        const todayKey = new Date().toDateString();
        const state = {};
        (goal.time_slots || []).forEach(k => { state[k] = false; });
        checkins.forEach(c => {
            if (!c.slot) return;
            if (new Date(c.created_at).toDateString() === todayKey && Object.prototype.hasOwnProperty.call(state, c.slot)) {
                state[c.slot] = true;
            }
        });
        return state;
    }, [goal, checkins]);

    if (!goal) return <div className="p-8 font-ui text-sm text-ink-40">Loading…</div>;
    const completed = goal.status === "completed";
    const resting = goal.status === "resting";
    const cfg = legacyRhythmConfig(goal);
    const todaysSlots = scheduledSlotsForToday(cfg);

    const filledToday = new Set(
        checkins
            .filter(c => c.slot_time && new Date(c.created_at).toDateString() === new Date().toDateString())
            .map(c => c.slot_time)
    );

    const doCheckin = async (opts = {}) => {
        setBusy(true);
        try {
            const payload = { note };
            if (opts.slot) payload.slot = opts.slot;
            if (opts.slotTime) payload.slot_time = opts.slotTime;
            if (showBackdate && backdateAt) {
                payload.created_at = new Date(backdateAt).toISOString();
            }
            await checkinGoal(goal.id, payload);
            setNote(""); setBackdateAt(""); setShowBackdate(false);
            await load();
        } finally { setBusy(false); }
    };
    const setStatus = async (status) => {
        await updateGoal(goal.id, { status });
        await load();
    };
    const complete = async () => {
        await updateGoal(goal.id, { status: "completed" });
        const meta = goal.category + (goal.theme ? ` · ${goal.theme}` : "");
        sessionStorage.setItem("goal-completion-prompt", JSON.stringify({
            body: `${goal.title} — a season with God has finished.\n\n${meta}\n\nWhat did God do in this?\n\n`,
            verse: encouragement?.passage,
        }));
        nav("/journal/new?fromGoal=1");
    };
    const remove = async () => {
        if (!window.confirm("Delete this goal? (History will be removed too.)")) return;
        await deleteGoal(goal.id);
        nav("/goals");
    };

    return (
        <div
            data-testid="goal-view"
            className="px-5 md:px-8 py-6 md:py-8 pb-32 mx-auto w-full"
            style={{ maxWidth: "72ch" }}
        >
            <div className="flex items-start justify-between gap-3 mb-3">
                <button
                    onClick={() => nav("/goals")}
                    className="inline-flex items-center gap-1.5 font-ui text-[13px] text-ink-70 hover:text-ink"
                >
                    <ArrowLeft size={14} /> Goals
                </button>
                {!completed && (
                    <div className="flex items-center gap-1">
                        <button
                            data-testid="goal-edit"
                            onClick={() => nav(`/goals/${goal.id}/edit`)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-ui text-[12px] text-ink-70 hover:text-ink hover:bg-ink-10 transition"
                            aria-label="Edit goal"
                            title="Edit goal"
                        >
                            <Edit3 size={13} /> Edit
                        </button>
                        <button
                            data-testid="goal-delete"
                            onClick={remove}
                            className="w-9 h-9 rounded-full flex items-center justify-center text-ink-40 hover:text-plum hover:bg-plum/10 transition"
                            aria-label="Delete goal"
                            title="Delete goal"
                        >
                            <Trash2 size={14} />
                        </button>
                    </div>
                )}
            </div>

            <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className="px-2 py-0.5 rounded-full bg-moss/10 text-moss-deep font-ui text-[11px] font-medium">
                    {goal.category}
                </span>
                {goal.theme && (
                    <span className="font-ui text-[11px] text-ink-40 italic">{goal.theme}</span>
                )}
                {goal.rhythm !== "none" && (
                    <span className="font-ui text-[11px] text-ink-40 inline-flex items-center gap-1">
                        <CalendarClock size={11} /> {goal.rhythm === "times_of_day" ? "times of day" : goal.rhythm}
                    </span>
                )}
                {goal.target_date && !completed && (() => {
                    const days = daysUntil(goal.target_date);
                    const amber = days !== null && days >= 0 && days <= 7;
                    return (
                        <span
                            data-testid="goal-view-horizon"
                            data-amber={amber ? "yes" : "no"}
                            className={
                                "px-2 py-0.5 rounded-full font-ui text-[11px] inline-flex items-center gap-1 " +
                                (amber ? "text-ink font-medium" : "text-ink-40")
                            }
                            style={amber ? { backgroundColor: "rgba(231, 195, 111, 0.35)" } : undefined}
                            title={formatLongDate(goal.target_date)}
                        >
                            <CalendarClock size={11} /> {formatHorizon(goal.target_date)}
                        </span>
                    );
                })()}
                {resting && <span className="px-2 py-0.5 rounded-full bg-slate/20 text-ink-70 font-ui text-[11px]">Resting</span>}
                {completed && <span className="px-2 py-0.5 rounded-full bg-moss/20 text-moss-deep font-ui text-[11px]">Completed</span>}
            </div>
            <h1 data-testid="goal-title-display" className="scripture text-3xl md:text-4xl text-ink tracking-tight leading-tight">
                {goal.title}
            </h1>
            {goal.why && (
                <p className="mt-3 scripture text-[15px] italic text-ink-70 leading-relaxed">"{goal.why}"</p>
            )}
            {resting && (
                <div className="mt-4 p-3 rounded-2xl bg-sage-paper font-ui text-[13px] text-ink-70 italic">
                    Paused, not abandoned. Resume anytime.
                </div>
            )}

            {/* Weekly reflection nudge — once per ISO week per active goal */}
            <WeeklyReflectCard goal={goal} />

            {/* Passed target date — gentle crossroads */}
            {(() => {
                if (completed || resting) return null;
                if (!goal.target_date) return null;
                const days = daysUntil(goal.target_date);
                if (days === null || days >= 0) return null;
                if (crossroadsDismissed) return null;
                return (
                    <div
                        data-testid="crossroads"
                        className="mt-4 p-5 rounded-3xl bg-sage-paper border border-moss/20"
                    >
                        <div className="font-ui text-[10px] uppercase tracking-[0.2em] text-moss-deep font-semibold">
                            A crossroads
                        </div>
                        <p className="mt-1 scripture text-[18px] text-ink leading-snug">
                            {formatLongDate(goal.target_date)} has come. How did it go?
                        </p>
                        {!pickingNewDate ? (
                            <div className="mt-4 flex flex-wrap gap-2">
                                <button
                                    data-testid="crossroads-complete"
                                    onClick={complete}
                                    className="px-4 py-2 rounded-full bg-moss text-white font-ui text-[13px] font-medium hover:bg-moss-deep transition inline-flex items-center gap-1.5"
                                >
                                    <Sparkles size={14} /> Complete it
                                </button>
                                <button
                                    data-testid="crossroads-extend"
                                    onClick={() => { setNewHorizon(""); setPickingNewDate(true); }}
                                    className="px-4 py-2 rounded-full bg-white border border-ink-10 font-ui text-[13px] text-ink-70 hover:border-moss/40 transition inline-flex items-center gap-1.5"
                                >
                                    <CalendarClock size={14} /> Give it more time
                                </button>
                                <button
                                    data-testid="crossroads-set-down"
                                    onClick={async () => { await setStatus("resting"); }}
                                    className="px-4 py-2 rounded-full bg-white border border-ink-10 font-ui text-[13px] text-ink-70 hover:border-moss/40 transition inline-flex items-center gap-1.5"
                                >
                                    <Pause size={14} /> Pause it
                                </button>
                                <button
                                    data-testid="crossroads-dismiss"
                                    onClick={() => setCrossroadsDismissed(true)}
                                    className="ml-auto px-3 py-2 rounded-full font-ui text-[12px] text-ink-40 hover:text-ink hover:bg-ink-10 transition"
                                >
                                    Not now
                                </button>
                            </div>
                        ) : (
                            <div className="mt-4">
                                <p className="font-ui text-[13px] italic text-ink-70 mb-2">
                                    Seasons stretch. Set a new horizon.
                                </p>
                                <div className="flex flex-wrap items-center gap-2">
                                    <input
                                        data-testid="crossroads-new-date"
                                        type="date"
                                        value={newHorizon}
                                        onChange={e => setNewHorizon(e.target.value)}
                                        className="px-3 py-1.5 rounded-full bg-white border border-ink-10 font-ui text-[12px] focus:outline-none focus:border-moss/60"
                                    />
                                    <button
                                        data-testid="crossroads-remove-date"
                                        onClick={async () => {
                                            await updateGoal(goal.id, { target_date: "" });
                                            setPickingNewDate(false);
                                            await load();
                                        }}
                                        className="font-ui text-[11px] text-ink-40 hover:text-ink underline-offset-2 hover:underline"
                                    >
                                        Remove the date
                                    </button>
                                    <div className="ml-auto flex gap-2">
                                        <button
                                            onClick={() => setPickingNewDate(false)}
                                            className="px-3 py-1.5 rounded-full font-ui text-[12px] text-ink-70 hover:bg-ink-10"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            data-testid="crossroads-save-date"
                                            disabled={!newHorizon}
                                            onClick={async () => {
                                                await updateGoal(goal.id, { target_date: newHorizon });
                                                setPickingNewDate(false);
                                                await load();
                                            }}
                                            className={
                                                "px-3 py-1.5 rounded-full font-ui text-[12px] " +
                                                (newHorizon ? "bg-moss text-white hover:bg-moss-deep" : "bg-ink-10 text-ink-40 cursor-not-allowed")
                                            }
                                        >
                                            Save horizon
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* Today's scheduled slots */}
            {!completed && todaysSlots.length > 0 && (
                <div
                    data-testid="today-slots"
                    className="mt-6 p-4 rounded-3xl bg-white border border-ink-10 flex flex-wrap items-center gap-4"
                >
                    <span className="font-ui text-[10px] uppercase tracking-[0.2em] text-ink-40 font-semibold">Today</span>
                    {todaysSlots.map((t, i) => {
                        const hhmm = t.time;
                        const filled = filledToday.has(hhmm);
                        return (
                            <button
                                key={`${hhmm}-${i}`}
                                data-testid={`slot-marker-${hhmm.replace(":", "")}`}
                                data-filled={filled ? "yes" : "no"}
                                onClick={() => doCheckin({ slotTime: hhmm })}
                                disabled={busy}
                                className="inline-flex items-center gap-2 group"
                                title={filled ? `${t.label || fmtTime(hhmm)} — checked in` : `Check in for ${t.label || fmtTime(hhmm)}`}
                            >
                                <span
                                    className={
                                        "inline-flex items-center justify-center w-8 h-8 rounded-full text-[10px] font-ui font-semibold transition " +
                                        (filled
                                            ? "bg-moss text-white"
                                            : "bg-white border-2 border-ink-10 text-ink-40 group-hover:border-moss/40 group-hover:text-moss-deep")
                                    }
                                >
                                    {hhmm.split(":")[0]}
                                </span>
                                <span className={"font-ui text-[13px] " + (filled ? "text-moss-deep font-medium" : "text-ink-70")}>
                                    {t.label || fmtTime(hhmm)}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Legacy times-of-day markers (goals without rhythm_config) */}
            {!completed && !goal.rhythm_config && goal.rhythm === "times_of_day" && (
                <div
                    data-testid="today-slots-legacy"
                    className="mt-6 p-4 rounded-3xl bg-white border border-ink-10 flex flex-wrap items-center gap-4"
                >
                    <span className="font-ui text-[10px] uppercase tracking-[0.2em] text-ink-40 font-semibold">Today</span>
                    {(goal.time_slots || []).map(sk => {
                        const meta = ALL_SLOTS.find(s => s.key === sk);
                        if (!meta) return null;
                        const filled = todaySlotState[sk];
                        const Icon = meta.icon;
                        return (
                            <button
                                key={sk}
                                data-testid={`slot-checkin-${sk}`}
                                onClick={() => doCheckin({ slot: sk })}
                                disabled={busy}
                                className="inline-flex items-center gap-2 group"
                            >
                                <span
                                    className={
                                        "inline-flex items-center justify-center w-8 h-8 rounded-full transition " +
                                        (filled ? "bg-moss text-white" : "bg-white border-2 border-ink-10 text-ink-40 group-hover:border-moss/40")
                                    }
                                >
                                    <Icon size={14} />
                                </span>
                                <span className={"font-ui text-[13px] " + (filled ? "text-moss-deep font-medium" : "text-ink-70")}>
                                    {meta.label}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Quantity tracker */}
            {!completed && goal.goal_type === "quantity" && (
                <>
                    <QuantityTracker goal={goal} />
                    <div className="mt-4 flex flex-wrap gap-2">
                        {!resting ? (
                            <button
                                data-testid="goal-pause"
                                onClick={() => setStatus("resting")}
                                className="px-4 py-2 rounded-full bg-white border border-ink-10 font-ui text-[13px] text-ink-70 hover:border-moss/40 transition inline-flex items-center gap-1.5"
                            >
                                <Pause size={14} /> Pause
                            </button>
                        ) : (
                            <button
                                data-testid="goal-resume"
                                onClick={() => setStatus("active")}
                                className="px-4 py-2 rounded-full bg-white border border-ink-10 font-ui text-[13px] text-ink-70 hover:border-moss/40 transition inline-flex items-center gap-1.5"
                            >
                                <Play size={14} /> Resume
                            </button>
                        )}
                        <button
                            data-testid="goal-complete"
                            onClick={complete}
                            className="px-4 py-2 rounded-full bg-white border border-ink-10 font-ui text-[13px] text-ink-70 hover:border-moss/40 transition inline-flex items-center gap-1.5"
                        >
                            <Sparkles size={14} /> Complete
                        </button>
                    </div>
                </>
            )}

            {/* Encouragement card */}
            {encouragement?.passage && (
                <article
                    data-testid="encouragement-card"
                    className="mt-6 p-5 rounded-3xl bg-white border border-ink-10 shadow-sm"
                >
                    <div className="font-ui text-[10px] uppercase tracking-[0.2em] text-moss-deep font-semibold flex items-center gap-1.5">
                        <Sparkles size={11} /> A word for the way
                    </div>
                    <blockquote className="mt-2 scripture text-[19px] md:text-[20px] text-ink leading-snug">
                        "{encouragement.passage.text}"
                    </blockquote>
                    <div className="mt-2 font-ui text-[12px] text-moss-deep font-medium">
                        — {encouragement.passage.reference} ({(encouragement.passage.translation || "kjv").toUpperCase()})
                    </div>
                    {encouragement.line && (
                        <p className="mt-3 font-ui text-[13px] italic text-ink-70">{encouragement.line}</p>
                    )}
                </article>
            )}

            {/* Standard-goal check-in composer + action buttons */}
            {!completed && goal.goal_type !== "quantity" && (
                <div className="mt-6">
                    <label className="block font-ui text-[10px] uppercase tracking-[0.2em] text-ink-40 mb-1">
                        {todaysSlots.length > 0 ? "Add a note to your next check-in" : "Check in"} <span className="normal-case tracking-normal text-ink-40/70">(optional)</span>
                    </label>
                    <textarea
                        data-testid="checkin-note"
                        value={note}
                        onChange={e => setNote(e.target.value)}
                        rows={2}
                        placeholder="A short reflection, if you'd like…"
                        className="w-full px-4 py-3 rounded-2xl bg-white border border-ink-10 scripture text-[15px] focus:outline-none focus:border-moss/60 resize-none"
                    />

                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <button
                            data-testid="toggle-backdate"
                            onClick={() => setShowBackdate(v => !v)}
                            className="font-ui text-[12px] text-ink-40 hover:text-ink underline-offset-2 hover:underline"
                        >
                            {showBackdate ? "Log for now" : "Log for a past date/time?"}
                        </button>
                        {showBackdate && (
                            <input
                                data-testid="backdate-input"
                                type="datetime-local"
                                value={backdateAt}
                                onChange={e => setBackdateAt(e.target.value)}
                                className="px-3 py-1.5 rounded-full bg-white border border-ink-10 font-ui text-[12px] focus:outline-none focus:border-moss/60"
                            />
                        )}
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                        <button
                            data-testid="checkin-submit"
                            disabled={busy}
                            onClick={() => doCheckin()}
                            className="px-4 py-2 rounded-full bg-moss text-white font-ui text-[13px] font-medium hover:bg-moss-deep transition inline-flex items-center gap-1.5"
                        >
                            <Check size={14} /> {todaysSlots.length > 0 ? "Check in (unscheduled)" : "Check in"}
                        </button>
                        {todaysSlots.length > 0 && (
                            <p className="font-ui text-[12px] italic text-ink-40 self-center">
                                Or tap a slot marker above.
                            </p>
                        )}
                        {!resting ? (
                            <button
                                data-testid="goal-pause"
                                onClick={() => setStatus("resting")}
                                className="px-4 py-2 rounded-full bg-white border border-ink-10 font-ui text-[13px] text-ink-70 hover:border-moss/40 transition inline-flex items-center gap-1.5"
                            >
                                <Pause size={14} /> Pause
                            </button>
                        ) : (
                            <button
                                data-testid="goal-resume"
                                onClick={() => setStatus("active")}
                                className="px-4 py-2 rounded-full bg-white border border-ink-10 font-ui text-[13px] text-ink-70 hover:border-moss/40 transition inline-flex items-center gap-1.5"
                            >
                                <Play size={14} /> Resume
                            </button>
                        )}
                        <button
                            data-testid="goal-complete"
                            onClick={complete}
                            className="px-4 py-2 rounded-full bg-white border border-ink-10 font-ui text-[13px] text-ink-70 hover:border-moss/40 transition inline-flex items-center gap-1.5"
                        >
                            <Sparkles size={14} /> Complete
                        </button>
                    </div>
                </div>
            )}

            {completed && (
                <div className="mt-6 p-5 rounded-3xl bg-moss/5 border border-moss/20">
                    <div className="font-ui text-[10px] uppercase tracking-[0.2em] text-moss-deep font-semibold">
                        Completed
                    </div>
                    <p className="mt-2 scripture text-[17px] text-ink leading-snug">
                        "Being confident of this very thing, that he which hath begun a good work in you will perform it until the day of Jesus Christ." — Philippians 1:6
                    </p>
                    {(goal.target_date || goal.completed_at) && (
                        <p
                            data-testid="completed-dates"
                            className="mt-3 font-ui text-[12px] text-ink-40"
                        >
                            {goal.target_date && <>aimed for {formatLongDate(goal.target_date)}</>}
                            {goal.target_date && goal.completed_at && " · "}
                            {goal.completed_at && (() => {
                                const d = new Date(goal.completed_at);
                                return `completed ${d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
                            })()}
                        </p>
                    )}
                </div>
            )}

            {/* Check-in history (standard goals only — quantity goals use logs) */}
            {goal.goal_type !== "quantity" && (
                <section className="mt-8">
                    <h2 className="font-ui text-[10px] uppercase tracking-[0.25em] text-ink-40 font-semibold mb-2">
                        History · {checkins.length}
                    </h2>
                    {checkins.length === 0 ? (
                        <p className="font-ui text-[13px] italic text-ink-40">
                            Welcome. Your first check-in will land here.
                        </p>
                    ) : (
                        <ul className="space-y-2">
                            {checkins.map(c => (
                                <CheckinRow
                                    key={c.id}
                                    goalId={goal.id}
                                    c={c}
                                    editing={editingId === c.id}
                                    onEdit={() => setEditingId(editingId === c.id ? null : c.id)}
                                    onSaved={async () => { setEditingId(null); await load(); }}
                                    onDeleted={async () => { await load(); }}
                                />
                            ))}
                        </ul>
                    )}
                </section>
            )}
        </div>
    );
}
